"use client";

import { Turnstile } from "@marsidev/react-turnstile";
import { isBlockedConsumerEmail } from "@/lib/blocked-email-domains";
import { useState } from "react";

type Props = {
  turnstileSiteKey: string;
  captchaSkip: boolean;
};

export function ContactForm({ turnstileSiteKey, captchaSkip }: Props) {
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const needsWidget =
    !captchaSkip && Boolean(turnstileSiteKey?.trim());
  const blocked =
    !captchaSkip && !turnstileSiteKey?.trim();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (blocked) return;
    const emailVal = String(
      new FormData(e.currentTarget).get("email") ?? "",
    ).trim();
    if (isBlockedConsumerEmail(emailVal)) {
      setStatus("err");
      setMessage(
        "Please use a business or corporate email address. Personal webmail (Gmail, Yahoo, Outlook, etc.) is not accepted.",
      );
      return;
    }
    if (needsWidget && !token) {
      setStatus("err");
      setMessage("Complete the verification widget first.");
      return;
    }
    const fd = new FormData(e.currentTarget);
    setPending(true);
    setStatus("idle");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fd.get("name"),
          email: fd.get("email"),
          subject: fd.get("subject"),
          message: fd.get("message"),
          turnstileToken: captchaSkip ? "" : token ?? "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus("err");
        setMessage(
          typeof data?.error === "string" ? data.error : "Something went wrong.",
        );
        return;
      }
      setStatus("ok");
      setMessage("Your message has been sent. We’ll get back to you shortly.");
      (e.target as HTMLFormElement).reset();
      setToken(null);
    } catch {
      setStatus("err");
      setMessage("Network error. Please try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xl shadow-slate-200/50 md:p-8">
      {status !== "idle" ? (
        <div
          role={status === "err" ? "alert" : "status"}
          className={`mb-6 rounded-xl px-4 py-3 text-sm ${
            status === "ok"
              ? "border border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {message}
        </div>
      ) : null}

      {blocked ? (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Contact verification is not configured. Add Turnstile keys in{" "}
          <strong>Admin → Settings → Contact Form CAPTCHA</strong>, or enable bypass for
          development.
        </p>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-slate-700">
            Full name
          </label>
          <input
            id="name"
            name="name"
            required
            autoComplete="name"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 outline-none ring-[var(--accent)] focus:ring-2"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">
            Business email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 outline-none ring-[var(--accent)] focus:ring-2"
          />
          <p className="mt-1.5 text-xs text-slate-500">
            Use your company domain — personal addresses (Gmail, Yahoo, Outlook, etc.)
            are not accepted.
          </p>
        </div>
        <div>
          <label htmlFor="subject" className="mb-1 block text-sm font-medium text-slate-700">
            Subject
          </label>
          <input
            id="subject"
            name="subject"
            required
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 outline-none ring-[var(--accent)] focus:ring-2"
          />
        </div>
        <div>
          <label htmlFor="message" className="mb-1 block text-sm font-medium text-slate-700">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            className="w-full resize-y rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 outline-none ring-[var(--accent)] focus:ring-2"
          />
        </div>

        {needsWidget ? (
          <div className="flex justify-start">
            <Turnstile
              siteKey={turnstileSiteKey}
              onSuccess={(t) => setToken(t)}
              onExpire={() => setToken(null)}
            />
          </div>
        ) : null}

        <button
          type="submit"
          disabled={pending || blocked}
          className="w-full rounded-xl bg-[var(--accent)] py-3 text-center text-sm font-bold text-[var(--accent-foreground)] shadow-md transition hover:bg-[var(--accent-light)] disabled:opacity-50"
        >
          {pending ? "Sending…" : "Send message"}
        </button>
      </form>
    </div>
  );
}
